<template>
  <div :class="{ 'card mt-3': readOnly }">

    <div v-if="readOnly" class="card-header pt-3 d-flex align-items-start justify-content-between">
      <h5><strong>Grupos de permissão</strong></h5>
      <button v-if="canManageGroups" type="button" class="btn btn-outline-secondary btn-sm text-nowrap"
              @click="openGroups()">
        Gerenciar grupos de permissão
        <external-link-icon class="icon-sm ml-1"/>
      </button>
    </div>

    <div :class="{ 'card-body': readOnly }">

      <template v-if="readOnly">
        <section class="mb-3">
          <h6><strong>Grupos do usuário</strong></h6>
          <div v-for="group in selectedGroups" :key="group.id"
               class="d-flex align-items-center border rounded px-3 py-2 mb-2 assigned">
            <span class="badge badge-primary mr-2">Atribuído</span>
            <strong class="small">{{ group.name }}</strong>
            <small v-if="group.system" class="text-muted ml-1">(sistema)</small>
            <a v-if="canManageGroups" href="#" class="small ml-auto text-nowrap" @click.prevent="openGroups(group.id)">
              Ver grupo
            </a>
          </div>
          <p v-if="selectedGroups.length === 0" class="small text-muted mb-0">Nenhum grupo atribuído.</p>
        </section>

        <section v-if="otherGroups.length" class="border-top pt-3 mb-3">
          <h6><strong>Outros grupos da conta</strong></h6>
          <span v-for="group in otherGroups" :key="group.id" class="badge badge-light text-muted mr-1 mb-1">
            {{ group.name }}<template v-if="group.system"> (sistema)</template>
          </span>
        </section>

        <h6 v-if="summaries.length" class="border-top pt-3"><strong>Resumo de acesso</strong></h6>
      </template>

      <div v-else class="mb-2">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <label class="small mb-0">Grupos de permissão</label>
          <button v-if="canManageGroups" type="button" class="btn btn-outline-secondary btn-sm text-nowrap"
                  @click="openGroups()">
            Gerenciar grupos de permissão
            <external-link-icon class="icon-sm ml-1"/>
          </button>
        </div>
        <div class="border rounded p-1 group-list">
          <label v-for="group in groups" :key="group.id" class="d-flex align-items-center mb-0 px-2 py-1 pointer">
            <bkps-check-box :value="value.includes(group.id)" @input="toggle(group.id)"/>
            <span class="small ml-2">
              {{ group.name }}
              <small v-if="group.system" class="text-muted">(sistema)</small>
            </span>
          </label>
        </div>
      </div>

      <!-- Prévia: quem decide é o Permissions::Effective no servidor. Isto existe
           para o administrador ver o efeito de trocar de grupo antes de salvar. -->
      <div v-for="summary in summaries" :key="summary.key" class="border rounded mb-2">
        <div class="border-bottom px-3 py-2">
          <p class="small mb-0">
            <lock-icon v-if="summary.anySystem" class="icon-sm text-muted"/>
            <strong>{{ summary.title }}</strong>
            — {{ summary.permissions }} permissões
            <template v-if="summary.hidden > 0">
              · <span class="text-danger">{{ summary.hidden }} valor(es) oculto(s)</span>
            </template>
          </p>
          <small v-if="summary.description" class="text-muted">{{ summary.description }}</small>
        </div>

        <div class="px-3 py-2">
          <p class="small text-muted mb-1">
            <strong class="text-dark">Empresas e filiais:</strong>
            <template v-if="summary.company.allCompanies">todas as empresas da conta</template>
            <template v-else-if="summary.company.cnpjs.length === 0">nenhuma empresa</template>
            <template v-else>
              {{ summary.company.cnpjs.length }} filial(is):
              <span v-for="cnpj in summary.company.cnpjs" :key="cnpj" class="badge badge-light mr-1">
                {{ cnpj | cnpj }}
              </span>
            </template>
          </p>

          <p v-if="summary.scopes.length === 0" class="small text-muted mb-1">
            Sem recorte de dado — enxerga toda a base.
          </p>
          <p v-for="row in summary.scopes" :key="row.key" class="small text-muted mb-1">
            <strong class="text-dark">Não vê {{ row.label.toLowerCase() }}:</strong>
            <span v-for="item in row.values" :key="item" class="badge badge-light text-danger mr-1">{{ item }}</span>
          </p>
        </div>
      </div>

      <small class="d-block text-muted" :class="{ 'border-top pt-2 mt-3': readOnly }">
        Permissões e recorte de dado são definidos nos grupos, não aqui — editar um grupo vale
        para todas as pessoas que pertencem a ele.
        <template v-if="readOnly">Para trocar os grupos desta pessoa, use o botão Editar.</template>
        <template v-else>Aqui você só escolhe de quais grupos a pessoa participa.</template>
      </small>

    </div>

  </div>
</template>

<script>
  import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
  import { ExternalLinkIcon, LockIcon } from 'vue-feather-icons'
  import { effectiveCompanyScope, effectivePermissions, effectiveScopes, hiddenCount } from '@/models/permissionsEffective'

  export default {
    components: { BkpsCheckBox, ExternalLinkIcon, LockIcon },
    computed: {
      canManageGroups() {
        return this.$canNew('application', 'permission_groups')
      },
      otherGroups() {
        return this.groups.filter(group => !this.value.includes(group.id))
      },
      selectedGroups() {
        return this.groups.filter(group => this.value.includes(group.id))
      },
      // Na tela de leitura cada grupo tem o seu resumo; com mais de um, o efetivo
      // vem junto porque o recorte da soma não é a soma dos recortes.
      summaries() {
        const groups = this.selectedGroups
        const combined = this.summarize('combined', groups, this.combinedTitle(groups), this.combinedDescription(groups))

        if (!this.readOnly || groups.length === 0) return [combined]

        const perGroup = groups.map(group => this.summarize(group.id, [group], group.name, group.description))
        return groups.length > 1 ? [...perGroup, combined] : perGroup
      }
    },
    created() {
      this.load()
    },
    data: () => ({
      dimensions: [],
      groups: []
    }),
    methods: {
      combinedDescription(groups) {
        if (groups.length === 1) return groups[0].description
        if (groups.length > 1)
          return `Soma de ${groups.length} grupos. Um valor só continua oculto quando todos os ` +
            'grupos o ocultam — grupo sem recorte afrouxa o recorte dos outros.'

        return this.readOnly ? 'Sem grupo atribuído.' : 'Sem grupo escolhido, a pessoa entra no grupo "Membro".'
      },
      combinedTitle(groups) {
        if (groups.length === 0) return 'Nenhum grupo'
        if (this.readOnly && groups.length > 1) return 'Acesso efetivo'

        return groups.map(group => group.name).join(' + ')
      },
      labelFor(key) {
        const dimension = this.dimensions.find(current => current.key === key)
        return dimension ? dimension.label : key
      },
      load() {
        this.axios.get('permission_groups', { params: { per_page: 200, meta: true } })
          .then(response => this.groups = response.data || [])
          .catch(e => this.$notify.addError(e))

        this.axios.get('permission_groups/catalog')
          .then(response => this.dimensions = (response.data || {}).scope_dimensions || [])
          .catch(() => this.dimensions = [])
      },
      openGroups(id) {
        const query = id ? `?tab=permission_groups&group=${id}` : '?tab=permission_groups'
        history.replaceState({}, null, this.$route.path + query)
        this.$router.go(0)
      },
      // Um mesmo valor pode estar oculto em mais de um produto; o resumo agrupa
      // por dimensão porque é assim que a pessoa pensa ("não vê NIC").
      scopeRows(scopes) {
        const byDimension = {}
        Object.values(scopes).forEach(dimensions => {
          Object.keys(dimensions).forEach(key => {
            byDimension[key] = byDimension[key] || new Set()
            dimensions[key].forEach(value => byDimension[key].add(value))
          })
        })

        return Object.keys(byDimension).map(key => ({
          key,
          label: this.labelFor(key),
          values: [...byDimension[key]]
        }))
      },
      summarize(key, groups, title, description) {
        const scopes = effectiveScopes(groups)

        return {
          anySystem: groups.some(group => group.system),
          company: effectiveCompanyScope(groups),
          description,
          hidden: hiddenCount(scopes),
          key,
          permissions: effectivePermissions(groups).length,
          scopes: this.scopeRows(scopes),
          title
        }
      },
      toggle(id) {
        const next = this.value.includes(id)
          ? this.value.filter(current => current !== id)
          : [...this.value, id]

        this.$emit('input', next)
      }
    },
    name: 'UserAccessPanel',
    props: {
      readOnly: { default: false },
      value: { default: () => [] }
    }
  }
</script>

<style scoped>
  .assigned { background: var(--light, #f8f9fa); }
  .pointer { cursor: pointer; }
  .icon-sm { width: 14px; height: 14px; }
  .group-list { max-height: 10rem; overflow-y: auto; }
</style>
